import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import MarkdownContent from 'src/features/product/product-detail/ui/updates-tab/markdown-content/MarkdownContent';
import useRfq from 'src/features/rfq-builder/hooks/useRfq';
import { usePageTitle } from 'src/shared/hooks';

import { Spinner } from 'src/shared/components/ui';

const BuildAnythingPrintPage = () => {
  const [searchParams] = useSearchParams();
  const rfqId = searchParams.get('rfq');

  const { data } = useRfq(rfqId as string);
  const rfqName = `${data?.rfq?.document?.name} RFQ` || 'RFQ Document';

  usePageTitle(rfqName);
  const generatedContent = data?.rfq?.rfq_markdown || '';

  const currentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  // Trigger print dialog automatically after content loads
  useEffect(() => {
    if (generatedContent && data?.rfq) {
      // Give the page a moment to render before opening print dialog
      const timer = setTimeout(() => {
        window.print();
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [generatedContent, data]);

  if (!data?.rfq) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Spinner />
      </div>
    );
  }

  if (!generatedContent) {
    return (
      <div className="flex justify-center items-center h-screen flex-col gap-4">
        <p className="text-lg">No RFQ content generated yet.</p>
        <p className="text-sm text-gray-600">
          Please generate the RFQ first before printing.
        </p>
      </div>
    );
  }

  // Generate watermark grid pattern with logo
  const watermarkRows = Array.from({ length: 10 }, (_, rowIndex) => (
    <div key={rowIndex} className="flex justify-around items-center">
      {Array.from({ length: 5 }, (_, colIndex) => (
        <img
          key={colIndex}
          src="/logo.svg"
          alt=""
          className="mx-12 my-10"
          style={{ width: '120px', height: 'auto' }}
        />
      ))}
    </div>
  ));

  return (
    <div className="max-w-4xl mx-auto p-8 bg-white min-h-screen relative">
      {/* Watermark overlay */}
      <div
        className="fixed inset-0 pointer-events-none z-0 overflow-hidden print:block"
        style={{
          opacity: 0.06
        }}
        aria-hidden="true"
      >
        <div
          className="absolute inset-0 flex flex-col justify-center items-center"
          style={{
            transform: 'rotate(-45deg) scale(1.5)',
            transformOrigin: 'center center'
          }}
        >
          {watermarkRows}
        </div>
      </div>

      {/* Main content */}
      <div className="relative z-10">
        <div className="mb-8 pb-4 border-b-2 border-blue-600 relative">
          <img
            src="/logo.svg"
            alt="Cavela"
            className="absolute top-0 right-0 h-8 w-auto"
          />
          <h1 className="text-3xl font-bold text-blue-600 mb-2 pr-24">
            {rfqName}
          </h1>
          <p className="text-sm text-gray-600">
            Request for Quotation • Generated on {currentDate}
          </p>
        </div>

        <div className="prose prose-slate max-w-none">
          <MarkdownContent markdownString={generatedContent} />
        </div>

        <div className="mt-8 pt-4 border-t border-gray-300 text-center text-sm text-gray-600">
          <p>Generated by Cavela • cavela.com</p>
        </div>
      </div>
    </div>
  );
};

export default BuildAnythingPrintPage;
